import React, { useState, useMemo, useEffect, useCallback } from 'react';

import {
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  Calendar,
  User,
  Phone,
  Filter,
  Download,
  RefreshCw,
  CreditCard,
  Banknote,
  TrendingUp,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Search
} from 'lucide-react';
import { safeAmount, formatCurrency, formatDate } from '../../utils/helpers';
import { PAYMENT_MILESTONES } from '../../lib/constants';
import { bookingService } from '../../services/booking.service';
import PaymentDetailModal from '../modals/PaymentDetailModal';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker } from '../ui';
import { useLanguage } from '../../contexts/LanguageContext';
import { leadsService } from '../../services/api';

export default function FinanceView({ leads, isLoading, onSyncPayments }) {
  const { t, language } = useLanguage();
  // The dashboard money cards deep-link here as /finance?from&to&status so the
  // list you land on matches the figure you clicked. Read lazily at mount so the
  // first fetch already uses the right dates instead of fetching twice.
  const urlParam = (key) => new URLSearchParams(window.location.search).get(key);

  const [filter, setFilter] = useState(() => urlParam('status') || 'all');
  const [searchQuery, setSearchQuery] = useState('');

  // Default date range: current month
  const getDefaultDates = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    return {
      from: `${y}-${m}-01`,
      to: `${y}-${m}-${String(lastDay).padStart(2, '0')}`,
    };
  };
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(() => urlParam('from') || defaults.from);
  const [endDate, setEndDate] = useState(() => urlParam('to') || defaults.to);

  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [externalData, setExternalData] = useState([]);
  const [loadingExternal, setLoadingExternal] = useState(true);
  const [externalError, setExternalError] = useState(null);

  // Fetch external financial data — disabled for demo since BMS integration is off
  const fetchExternalData = useCallback(async () => {
    try {
      setLoadingExternal(true);
      setExternalError(null);
      // Removed BMS fetch
      setExternalData([]);
    } catch (error) {
      console.error('❌ Error fetching external financials:', error);
      setExternalError(error.message || 'Failed to load financial data');
      setExternalData([]);
    } finally {
      setLoadingExternal(false);
    }
  }, []);

  useEffect(() => {
    fetchExternalData();
  }, [fetchExternalData]);

  // Modal State — direct local state (URL params caused match failures when booking_id was null)
  const [selectedLeadForPayment, setSelectedLeadForPayment] = useState(null);

  // Pagination State
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Map local CRM data to display shape
  const bookedLeads = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const wonLeads = (leads || []).filter(l => l.stage === 'Won' || l.stage === 'Completed');

    return wonLeads.map((l) => {
      const totalValue = safeAmount(l.finalAmount || l.quotationAmount || 0);
      
      const payments = l.payments || [];
      const totalPaid = payments.length > 0 
        ? payments.reduce((sum, p) => sum + safeAmount(p.amount), 0)
        : safeAmount(l.advancePaid || l.advanceAmount || 0);
        
      const totalDue = Math.max(0, totalValue - totalPaid);

      let paymentStatus = 'pending';
      if (totalValue > 0 && totalDue <= 0) {
        paymentStatus = 'paid';
      } else if (totalPaid > 0) {
        paymentStatus = 'partial';
      }

      const eventDateStr = l.eventDate || null;
      if (eventDateStr) {
        const eventDate = new Date(eventDateStr);
        if (eventDate < today && totalDue > 0) paymentStatus = 'overdue';
      }

      return {
        ...l,
        id: l.id,
        clientName: l.clientName || 'Unknown',
        phone: l.phone || '-',
        bookingDate: l.bookedAt || l.createdAt || '',
        eventDate: eventDateStr,
        eventType: l.eventType || '',
        venue: l.venue || '',
        orderNo: l.orderNo || `ORD-${l.id}`,
        shift: l.shift || 'Day',
        totalValue,
        totalPaid,
        totalDue,
        paymentStatus,
        isExternalApi: false,
      };
    });
  }, [leads]);


  const displayLoading = isLoading || loadingExternal;

  // Reset page index when filters change
  useEffect(() => {
    setPageIndex(0);
  }, [filter, startDate, endDate, searchQuery, minAmount, maxAmount]);

  // Filtered data — date range is handled by API, only client-side filters remain
  const filteredLeads = useMemo(() => {
    let result = bookedLeads;

    // Search by client name or phone
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l =>
        (l.clientName && l.clientName.toLowerCase().includes(q)) ||
        (l.phone && l.phone.toLowerCase().includes(q)) ||
        (l.orderNo && l.orderNo.toLowerCase().includes(q))
      );
    }

    // Status filter
    if (filter === 'collected') {
      // Money actually received — what the dashboard's "Collected" card totals.
      result = result.filter(l => l.totalPaid > 0);
    } else if (filter === 'outstanding') {
      // Anything still owed, whether or not the event has passed. Distinct from
      // 'overdue', which only catches bookings whose event date is behind us.
      result = result.filter(l => l.totalDue > 0);
    } else if (filter !== 'all') {
      result = result.filter(l => l.paymentStatus === filter);
    }

    // Amount range
    if (minAmount !== '') {
      const min = Number(minAmount);
      if (!isNaN(min)) result = result.filter(l => l.totalValue >= min);
    }
    if (maxAmount !== '') {
      const max = Number(maxAmount);
      if (!isNaN(max)) result = result.filter(l => l.totalValue <= max);
    }

    return result;
  }, [bookedLeads, filter, searchQuery, minAmount, maxAmount]);

  const paginatedLeads = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredLeads.slice(start, start + pageSize);
  }, [filteredLeads, pageIndex, pageSize]);

  const totalPages = Math.ceil(filteredLeads.length / pageSize);

  // Calculate stats
  // Calculate stats based on FILTERED data (so users see stats for the selected period)
  // OR based on ALL booked leads? Usually stats are for the view. 
  // Let's make stats reflect the date range but NOT the status filter (so you see totals for that month)

  // Stats are computed from all data returned by the API (already date-filtered)
  const statsLeads = useMemo(() => {
    let result = bookedLeads;
    if (minAmount !== '') {
      const min = Number(minAmount);
      if (!isNaN(min)) result = result.filter(l => l.totalValue >= min);
    }
    if (maxAmount !== '') {
      const max = Number(maxAmount);
      if (!isNaN(max)) result = result.filter(l => l.totalValue <= max);
    }
    return result;
  }, [bookedLeads, minAmount, maxAmount]);

  const stats = useMemo(() => {
    // Use leads filtered by date, but NOT by status filter
    const sourceData = statsLeads;

    const totalBookings = sourceData.length;
    const totalValue = sourceData.reduce((sum, l) => sum + l.totalValue, 0);
    const totalPaid = sourceData.reduce((sum, l) => sum + l.totalPaid, 0);
    const totalDue = sourceData.reduce((sum, l) => sum + l.totalDue, 0);
    const overdueCount = sourceData.filter((l) => l.paymentStatus === 'overdue').length;
    const overdueAmount = sourceData
      .filter((l) => l.paymentStatus === 'overdue')
      .reduce((sum, l) => sum + l.totalDue, 0);

    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const upcomingThisMonth = sourceData.filter((l) => {
      // Ensure date exists
      if (!l.eventDate) return false;
      const eventDate = new Date(l.eventDate);
      return eventDate >= now && eventDate <= endOfMonth;
    });

    return {
      totalBookings,
      totalValue,
      totalPaid,
      totalDue,
      overdueCount,
      overdueAmount,
      upcomingThisMonth: upcomingThisMonth.length,
      collectionRate: totalValue > 0 ? ((totalPaid / totalValue) * 100).toFixed(1) : 0
    };
  }, [statsLeads]);

  const handleSync = async () => {
    setSyncing(true);
    await fetchExternalData();
    setSyncing(false);
    if (onSyncPayments) onSyncPayments();
  };

  const handleExport = async () => {
    console.log('🚀 Export process started');
    try {
      setSyncing(true);
      console.log('📡 Calling leadsService.exportFinance()...');
      const response = await leadsService.exportFinance();
      console.log('✅ Response received from server, conversion to blob...');
      const blob = await response.blob();
      console.log(`📦 Blob created: size=${blob.size}, type=${blob.type}`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `finance_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      console.log('🏁 Export download triggered');
    } catch (error) {
      console.error('❌ Export error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const baseClasses = "px-2.5 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1.5 w-fit mx-auto";

    switch (status) {
      case 'paid':
        return (
          <span className={`${baseClasses} bg-emerald-50 text-emerald-700 border-emerald-200`}>
            <CheckCircle className="w-3.5 h-3.5" /> {t('paid')}
          </span>
        );
      case 'partial':
        return (
          <span className={`${baseClasses} bg-amber-50 text-amber-700 border-amber-200`}>
            <Clock className="w-3.5 h-3.5" /> {t('partial')}
          </span>
        );
      case 'overdue':
        return (
          <span className={`${baseClasses} bg-red-50 text-red-700 border-red-200`}>
            <AlertTriangle className="w-3.5 h-3.5" /> {t('overdue')}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-slate-50 text-slate-600 border-slate-200`}>
            {t('pending')}
          </span>
        );
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">{t('financeDashboard')}</h1>
          <p className="text-gray-500 mt-1">{t('bookingPaymentsInvoicing')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg text-xs md:text-sm font-medium flex items-center gap-1 md:gap-2 hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{t('sync')}</span>
          </button>
          <button
            onClick={handleExport}
            disabled={syncing}
            className="px-3 md:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs md:text-sm font-medium flex items-center gap-1 md:gap-2 hover:bg-gray-200 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">{t('export')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4">
        {displayLoading ? (
          <>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-white p-3 md:p-5 rounded-xl border shadow-sm">
                <Skeleton variant="rounded" width={32} height={32} animation="wave" className="rounded-lg mb-2" />
                <Skeleton width="60%" height={24} animation="wave" className="mt-2 md:mt-3" />
                <Skeleton width="40%" height={12} animation="wave" className="mt-1" />
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="bg-white p-3 md:p-5 rounded-xl border shadow-sm">
              <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg w-fit">
                <CreditCard className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              </div>
              <p className="text-lg md:text-2xl font-bold mt-2 md:mt-3">{stats.totalBookings}</p>
              <p className="text-[10px] md:text-xs text-gray-500 mt-1">{t('totalBookings')}</p>
            </div>

            <div className="bg-white p-3 md:p-5 rounded-xl border shadow-sm">
              <div className="p-1.5 md:p-2 bg-green-100 rounded-lg w-fit">
                <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
              </div>
              <p className="text-lg md:text-2xl font-bold mt-2 md:mt-3 truncate">{formatCurrency(stats.totalValue)}</p>
              <p className="text-[10px] md:text-xs text-gray-500 mt-1">{t('totalValue')}</p>
            </div>

            <div className="bg-white p-3 md:p-5 rounded-xl border shadow-sm">
              <div className="p-1.5 md:p-2 bg-emerald-100 rounded-lg w-fit">
                <Banknote className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
              </div>
              <p className="text-lg md:text-2xl font-bold mt-2 md:mt-3 truncate">{formatCurrency(stats.totalPaid)}</p>
              <p className="text-[10px] md:text-xs text-gray-500 mt-1">{t('collected')}</p>
            </div>

            <div className="bg-white p-3 md:p-5 rounded-xl border shadow-sm">
              <div className="p-1.5 md:p-2 bg-amber-100 rounded-lg w-fit">
                <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-amber-600" />
              </div>
              <p className="text-lg md:text-2xl font-bold mt-2 md:mt-3">{stats.collectionRate}%</p>
              <p className="text-[10px] md:text-xs text-gray-500 mt-1">{t('collectionRate')}</p>
            </div>

            <div className="bg-white p-3 md:p-5 rounded-xl border shadow-sm border-red-200 col-span-2 sm:col-span-1">
              <div className="p-1.5 md:p-2 bg-red-100 rounded-lg w-fit">
                <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-red-600" />
              </div>
              <p className="text-lg md:text-2xl font-bold mt-2 md:mt-3 text-red-600">{stats.overdueCount}</p>
              <p className="text-[10px] md:text-xs text-gray-500 mt-1">{t('overdue')}</p>
            </div>
          </>
        )}
      </div>

      {/* Error Banner */}
      {externalError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{externalError}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 bg-white p-2 md:p-3 rounded-xl border">
        <Filter className="w-4 h-4 text-gray-400 my-auto flex-shrink-0" />
        {[
          { key: 'all', label: t('all') },
          { key: 'collected', label: t('collected') },
          { key: 'outstanding', label: t('outstanding') },
          { key: 'pending', label: t('pending') },
          { key: 'partial', label: t('partial') },
          { key: 'paid', label: t('paid') },
          { key: 'overdue', label: t('overdue') }
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-2 md:px-3 h-9 flex items-center justify-center rounded-lg text-xs md:text-sm font-medium whitespace-nowrap ${filter === f.key
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
          >
            {f.label}
          </button>
        ))}

        <div className="h-6 w-px bg-gray-200 mx-2 hidden sm:block"></div>

        {/* Date range — changes trigger API refetch via useEffect */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <DatePicker
            className="w-32 md:w-40"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fullWidth={false}
          />
          <span className="text-gray-400">-</span>
          <DatePicker
            className="w-32 md:w-40"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth={false}
          />
          {(startDate || endDate) && (
            <button
              onClick={() => {
                const d = getDefaultDates();
                setStartDate(d.from);
                setEndDate(d.to);
              }}
              className="text-xs text-red-500 px-1 hover:text-red-700 font-medium whitespace-nowrap"
            >
              {t('clear')} Date
            </button>
          )}
        </div>

        <div className="h-6 w-px bg-gray-200 mx-2 hidden lg:block"></div>

        {/* Amount Filters */}
        <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg text-sm px-2 h-9">
          <input
            type="number"
            placeholder="Min Rs"
            className="bg-transparent border-none text-xs md:text-sm p-1 w-20 md:w-24 outline-none file:hidden hover:appearance-none focus:ring-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
          />
          <span className="text-gray-400">-</span>
          <input
            type="number"
            placeholder="Max Rs"
            className="bg-transparent border-none text-xs md:text-sm p-1 w-20 md:w-24 outline-none focus:ring-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
          />
          {(minAmount || maxAmount) && (
            <button
              onClick={() => { setMinAmount(''); setMaxAmount(''); }}
              className="text-xs text-red-500 px-1 hover:text-red-700 font-medium whitespace-nowrap"
            >
              {t('clear')} Amount
            </button>
          )}
        </div>

        {/* Search Input */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search name, phone, order..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 pr-3 h-9 bg-gray-50 border border-gray-200 rounded-lg text-xs md:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-36 md:w-52 transition-all"
          />
        </div>

      </div>

      {/* Mobile Card View */}
      <div className={`md:hidden space-y-2 ${displayLoading ? 'opacity-70 pointer-events-none' : ''}`}>
        {displayLoading && bookedLeads.length === 0 ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white p-4 rounded-xl border shadow-sm space-y-3">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <Skeleton width={120} height={16} animation="wave" />
                    <Skeleton width={80} height={12} animation="wave" />
                  </div>
                  <Skeleton variant="rounded" width={60} height={20} animation="wave" className="rounded-full" />
                </div>
                <div className="pt-3 border-t flex justify-between">
                  <Skeleton width={60} height={30} animation="wave" />
                  <Skeleton width={60} height={30} animation="wave" />
                  <Skeleton width={60} height={30} animation="wave" />
                </div>
              </div>
            ))}
          </div>
        ) : paginatedLeads.length === 0 ? (
          <div className="text-center py-8 text-gray-400 bg-white rounded-xl border">
            {t('noBookingsFound')}
          </div>
        ) : (
          paginatedLeads.map((lead) => (
            <div key={lead.id} className="bg-white p-3 rounded-xl border shadow-sm cursor-pointer hover:shadow-md transition-shadow active:scale-[0.99]" onClick={() => setSelectedLeadForPayment(lead)}>
              <div className="flex justify-between items-start">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-gray-900 truncate">{lead.clientName}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <span className="text-gray-400">Booked:</span> {lead.bookingDate ? formatDate(lead.bookingDate, { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <span className="text-gray-400">Event:</span> <Calendar className="w-3 h-3" /> {lead.eventDate ? formatDate(lead.eventDate, { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                  </p>
                </div>
                {getPaymentStatusBadge(lead.paymentStatus)}
              </div>
              <div className="flex justify-between items-center mt-3 pt-2 border-t">
                <div>
                  <p className="text-[10px] text-gray-400">{t('total')}</p>
                  <p className="text-sm font-bold">{formatCurrency(lead.totalValue)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-gray-400">{t('collected')}</p>
                  <p className="text-sm font-bold text-green-600">{formatCurrency(lead.totalPaid)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400">{t('due')}</p>
                  <p className="text-sm font-bold text-red-600">{formatCurrency(lead.totalDue)}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className={`hidden md:block bg-white rounded-xl border shadow-sm overflow-hidden ${displayLoading ? 'opacity-70 pointer-events-none' : ''}`}>
        {displayLoading && bookedLeads.length === 0 ? (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between border-b pb-3 px-4">
              {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} width={100} height={16} animation="wave" />)}
            </div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b last:border-0 px-4">
                <div className="space-y-1"><Skeleton width={120} height={16} animation="wave" /><Skeleton width={80} height={12} animation="wave" /></div>
                <Skeleton width={100} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
                <Skeleton variant="rounded" width={70} height={24} animation="wave" className="rounded-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 font-medium border-b">
                <tr>
                  <th className="text-left px-4 py-3">{t('client')}</th>
                  <th className="text-left px-4 py-3 hidden xl:table-cell">Order No</th>
                  <th className="text-left px-4 py-3">Booking Date</th>
                  <th className="text-left px-4 py-3">{t('eventDate')}</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">{t('eventType')}</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Venue</th>
                  <th className="text-left px-4 py-3">{t('shift')}</th>
                  <th className="text-right px-4 py-3">{t('total')}</th>
                  <th className="text-right px-4 py-3">{t('collected')}</th>
                  <th className="text-right px-4 py-3">{t('due')}</th>
                  <th className="text-center px-4 py-3">{t('status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginatedLeads.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center py-8 text-gray-400">
                      No bookings found
                    </td>
                  </tr>
                ) : (
                  paginatedLeads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedLeadForPayment(lead)}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{lead.clientName}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs hidden xl:table-cell font-mono">{lead.orderNo || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700">
                          {lead.bookingDate ? formatDate(lead.bookingDate, { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-gray-700">
                          <Calendar className="w-3 h-3" />
                          {lead.eventDate ? formatDate(lead.eventDate, { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{lead.eventType || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{lead.venue || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 capitalize">{lead.shift ? (t(lead.shift.toLowerCase() + 'Shift') || lead.shift) : '-'}</td>
                      <td className="px-4 py-3 text-right font-bold">
                        {formatCurrency(lead.totalValue)}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600 font-medium">
                        {formatCurrency(lead.totalPaid)}
                      </td>
                      <td className="px-4 py-3 text-right text-red-600 font-medium">
                        {formatCurrency(lead.totalDue)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getPaymentStatusBadge(lead.paymentStatus)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredLeads.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          {/* Page Size Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Show</span>
            <Dropdown>
              <DropdownTrigger>
                <button className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center w-[70px]">
                  {pageSize}
                  <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[70px]" placement="top">
                <DropdownMenu
                  selectedKeys={new Set([pageSize.toString()])}
                  selectionMode="single"
                  onSelectionChange={(keys) => {
                    setPageSize(Number(Array.from(keys)[0]));
                    setPageIndex(0);
                  }}
                >
                  {[10, 20, 50].map((size) => (
                    <DropdownItem key={size} id={size.toString()}>
                      {size}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>
            <span className="text-sm text-gray-600">per page</span>
          </div>

          {/* Page Info */}
          <div className="text-sm text-gray-600">
            Page{' '}
            <span className="font-semibold text-gray-900">
              {pageIndex + 1}
            </span>{' '}
            of{' '}
            <span className="font-semibold text-gray-900">
              {totalPages}
            </span>
            {' · '}
            <span className="text-gray-500">
              {filteredLeads.length} results
            </span>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPageIndex(0)}
              disabled={pageIndex === 0}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPageIndex(p => Math.max(0, p - 1))}
              disabled={pageIndex === 0}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page Number Buttons */}
            <div className="hidden sm:flex items-center gap-1 mx-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i;
                } else if (pageIndex <= 2) {
                  pageNum = i;
                } else if (pageIndex >= totalPages - 3) {
                  pageNum = totalPages - 5 + i;
                } else {
                  pageNum = pageIndex - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setPageIndex(pageNum)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${pageIndex === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                      }`}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setPageIndex(p => Math.min(totalPages - 1, p + 1))}
              disabled={pageIndex >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPageIndex(totalPages - 1)}
              disabled={pageIndex >= totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Payment Milestones Info - Hidden on mobile */}
      <div className="hidden md:block bg-blue-50 border border-blue-200 rounded-xl p-4 md:p-5">
        <h3 className="font-bold text-blue-900 mb-3">Payment Milestone Structure</h3>
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          {PAYMENT_MILESTONES.map((milestone, i) => (
            <div key={milestone} className="bg-white p-3 md:p-4 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 md:w-6 md:h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {i + 1}
                </span>
                <span className="font-medium text-blue-900 text-sm">{milestone}</span>
              </div>
              <p className="text-xs md:text-sm text-blue-700">
                {i === 0 && '30% on booking'}
                {i === 1 && '40% week before'}
                {i === 2 && '30% event day'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Events Alert */}
      {stats.upcomingThisMonth > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 md:p-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-bold text-amber-900 text-sm md:text-base">
                {stats.upcomingThisMonth} events this month
              </p>
              <p className="text-xs md:text-sm text-amber-700">
                Ensure all payments are collected before event dates
              </p>
            </div>
          </div>
        </div>
      )}
      {selectedLeadForPayment && (
        <PaymentDetailModal
          lead={selectedLeadForPayment}
          onClose={() => setSelectedLeadForPayment(null)}
          onUpdate={(leadId, updates) => {
            if (onSyncPayments) onSyncPayments();
          }}
        />
      )}
    </div>
  );
}
